import { useEffect, useState, type ReactElement } from "react";

import { cn } from "@/lib/utils";
import { signal } from "@/motion/signals";

import { StartSiteAsk } from "./StartSiteAsk";

const HERO_LEAVE_MS = 280;

export function Hero(): ReactElement {
  const [talking, setTalking] = useState(false);
  const [heroCopy, setHeroCopy] = useState<"in" | "leaving" | "out">("in");
  const [returned, setReturned] = useState(false);

  useEffect(() => {
    if (talking) {
      setHeroCopy("leaving");
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      const hide = window.setTimeout(
        () => setHeroCopy("out"),
        reduce ? 0 : HERO_LEAVE_MS,
      );
      return () => window.clearTimeout(hide);
    }
    setHeroCopy("in");
  }, [talking]);

  function onActiveChange(active: boolean): void {
    if (!active && talking) setReturned(true);
    setTalking(active);
  }

  return (
    <section className="relative isolate overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_72%_58%_at_50%_76%,#9fc9f1_0%,#c5e0f8_32%,transparent_65%),radial-gradient(ellipse_72%_42%_at_50%_108%,#f6b5d4_0%,#d6c1eb_42%,transparent_74%),linear-gradient(180deg,#ffffff_0%,#eaf6ff_42%,#e5f3ff_100%)]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 -z-10 h-48 bg-gradient-to-b from-transparent to-white"
      />
      <div
        className={cn(
          "mx-auto flex min-h-[calc(100vh-4.5rem)] w-full max-w-4xl flex-col items-center justify-center px-5 pb-20 text-center sm:px-8 lg:pb-28",
          talking ? "pt-10 sm:pt-16" : "pt-16 sm:pt-24 lg:pt-28",
        )}
      >
        {heroCopy !== "out" ? (
          <div
            className={cn(
              "mb-10 sm:mb-12",
              heroCopy === "leaving" && signal.leave,
              returned && heroCopy === "in" && signal.arrive,
            )}
          >
            <div className="marketing-kicker mb-6 text-[#636793]">
              Your business, online
            </div>
            {/* Leading stays above 1 so descenders are not clipped where the
                heading sits inside an overflow-hidden or background-clip:text
                container. */}
            <h1 className="marketing-display text-[clamp(3rem,7vw,5.9rem)] leading-[1.04] text-[#20272b]">
              Build a site with <span className="block">a little more chill.</span>
            </h1>
            <p className="mx-auto mt-7 max-w-2xl text-lg leading-8 text-[#52606e] sm:text-xl sm:leading-9">
              Tell Chill about your business. It makes your site, then keeps it
              current when you send a message.
            </p>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-6 text-[#65736f]">
              Free for 14 days — we'd rather earn your business than assume
              it.
            </p>
          </div>
        ) : null}
        <div className={cn("w-full max-w-3xl text-left", talking && signal.arrive)}>
          <StartSiteAsk
            expandable
            expanded={talking}
            onActiveChange={onActiveChange}
          />
        </div>
        {heroCopy !== "out" ? (
          <p
            className={cn(
              "mt-6 text-xs tracking-wide text-[#65736f]",
              heroCopy === "leaving" && signal.leave,
              returned && heroCopy === "in" && signal.arrive,
            )}
          >
            Describe your business to get started. No editor to learn.
          </p>
        ) : null}
      </div>
    </section>
  );
}
